import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { FaArrowLeft } from 'react-icons/fa'
import { BsCalendar2Week, BsClock, BsJournalText, BsPencilFill, BsPlusLg, BsTrashFill } from 'react-icons/bs'
import axios from 'axios'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import Cookies from 'js-cookie'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import { useInstitute } from './commonComponents/InstituteContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { LuGraduationCap } from 'react-icons/lu'

function toTimeInputValue(value) {
  if (value == null || value === '') return ''
  const s = String(value).trim()
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5)
  if (/^\d{2}:\d{2}$/.test(s)) return s
  return s.length >= 5 ? s.slice(0, 5) : s
}

function timeToMinutes(t) {
  const parts = String(t).trim().split(':')
  if (parts.length < 2) return NaN
  const h = Number(parts[0])
  const m = Number(parts[1])
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN
  return h * 60 + m
}

function resolveCanManageExams(user) {
  if (!user) return false
  const t = (user.user_type || '').toLowerCase()
  const roles = (user.roles || []).map((r) => String(r).toLowerCase())
  return (
    t === 'admin' ||
    roles.includes('admin') ||
    t === 'teacher' ||
    roles.includes('teacher') ||
    t === 'superadmin' ||
    roles.includes('superadmin') ||
    t === 'manage_exams' ||
    roles.includes('manage_exams')
  )
}

function isFlagOn(value) {
  return value === true || value === 1 || value === '1'
}

function getSchedulePaperType(sch) {
  const explicit = String(sch?.exam_type ?? '').trim().toLowerCase()
  if (explicit === 'theory' || explicit === 'practical') return explicit
  return null
}

function validateScheduleRow(row) {
  if (!row.exam_date?.trim() || !row.start_time?.trim()) {
    return `Each subject needs an exam date and start time (${row.subject_name} is incomplete).`
  }
  if (row.end_time?.trim()) {
    const a = timeToMinutes(row.start_time)
    const b = timeToMinutes(row.end_time)
    if (Number.isFinite(a) && Number.isFinite(b) && a > b) {
      return `Start time must be before end time for ${row.subject_name}`
    }
  }
  return null
}

function buildSchedulePayload(row) {
  const entry = {
    exam_type: row.paper_type === 'practical' ? 'practical' : 'theory',
    exam_date: row.exam_date.trim(),
    start_time: row.start_time.trim(),
  }
  if (row.end_time?.trim()) entry.end_time = row.end_time.trim()
  return entry
}

function buildInstituteHeaders(token) {
  if (!token) return null
  const raw = Cookies.get('institute_id')
  const id = raw != null && raw !== '' ? Number(raw) : NaN
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (Number.isFinite(id)) {
    headers['institute-Id'] = id
  }
  return headers
}

const theme = {
  ink: '#223843',
  border: '#D1D3D4',
  muted: '#6b7280',
  surface: '#F3F4F6',
  text: '#111827',
  cardBg: '#ffffff',
}

export default function ExamSubjectSchedule() {
  const { examid } = useParams()
  const navigate = useNavigate()
  const { user, logout, token } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name: instituteName } = useInstitute()
  const [logoError, setLogoError] = useState(false)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingRowId, setSavingRowId] = useState(null)
  const [exam, setExam] = useState(null)
  const [courseName, setCourseName] = useState('')
  const [rows, setRows] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const canManageExams = useMemo(() => resolveCanManageExams(user), [user])

  // useEffect(() => {
  //   const handleResize = () => {
  //     if (window.innerWidth >= 992) {
  //       const expandedWidth = 245
  //       const collapsedWidth = 85
  //       const gap = 1
  //       setMarginLeft(
  //         isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
  //       )
  //     } else {
  //       setMarginLeft('0px')
  //     }
  //     setIsMobile(window.innerWidth <= 768)
  //   }
  //   handleResize()
  //   window.addEventListener('resize', handleResize)
  //   return () => window.removeEventListener('resize', handleResize)
  // }, [isCollapsed])


// WITH this (mirrors DataTable exactly):
useEffect(() => {
  const handleResize = () => {
    if (window.innerWidth >= 992) {
      const expandedWidth = 245
      const collapsedWidth = 85
      const gap = 1
      setMarginLeft(
        isCollapsed
          ? `${collapsedWidth + gap}px`
          : `${expandedWidth + gap}px`,
      )
    } else {
      setMarginLeft('0px')
    }
    setIsMobile(window.innerWidth <= 768)
  }
  handleResize()
  window.addEventListener('resize', handleResize)
  return () => window.removeEventListener('resize', handleResize)
}, [isCollapsed])

  const loadPage = useCallback(async () => {
    const idNum = Number(examid)
    if (!examid || !Number.isFinite(idNum)) {
      setExam(null)
      setRows([])
      setLoading(false)
      return
    }

    const headers = buildInstituteHeaders(token)
    if (!headers) {
      toast.error('You need to be signed in to load schedules.')
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const [coursesRes, examsRes, schedulesRes] = await Promise.all([
        axios.get(`${BASE_URL}/course/all`, { headers }),
        axios.get(`${BASE_URL}/get/exam/all`, { headers }),
        axios
          .get(`${BASE_URL}/exam/subject-schedules`, {
            headers,
            params: { exam_id: idNum },
          })
          .catch(() => ({ data: { data: [] } })),
      ])

      const courses = coursesRes.data?.data || []
      const exams = examsRes.data?.data || []
      const found = exams.find((e) => Number(e.id) === idNum) || null
      setExam(found)

      if (found?.course_id) {
        const cn = courses.find((c) => Number(c.id) === Number(found.course_id))?.name || ''
        setCourseName(cn)
      } else {
        setCourseName('')
      }

      const schedulePayload = schedulesRes.data?.data ?? schedulesRes.data ?? []
      const list = Array.isArray(schedulePayload) ? schedulePayload : []

      if (!found?.course_id) {
        setRows([])
        setLoading(false)
        return
      }

      const subRes = await axios.get(
        `${BASE_URL}/get/subjects/by-course?course_id=${encodeURIComponent(String(found.course_id))}`,
        { headers },
      )
      const subjects = subRes.data?.data || []

      const schedulesBySubject = new Map()
      list.forEach((sch) => {
        const csId = sch.course_subject_id ?? sch.course_subject?.id
        if (csId == null) return
        const key = Number(csId)
        const bucket = schedulesBySubject.get(key) || []
        bucket.push(sch)
        schedulesBySubject.set(key, bucket)
      })

      const nextRows = subjects.flatMap((sbj) => {
        const csId = Number(sbj.id)
        const types = []
        if (isFlagOn(sbj.is_theory)) types.push('theory')
        if (isFlagOn(sbj.is_practical)) types.push('practical')

        const subjectSchedules = schedulesBySubject.get(csId) || []
        const usedIds = new Set()

        return types.map((paperType) => {
          let sch = subjectSchedules.find((item) => {
            if (item?.id != null && usedIds.has(item.id)) return false
            return getSchedulePaperType(item) === paperType
          })
          if (!sch) {
            sch =
              subjectSchedules.find((item) => {
                if (item?.id != null && usedIds.has(item.id)) return false
                return getSchedulePaperType(item) == null
              }) || null
          }
          if (sch?.id != null) usedIds.add(sch.id)

          const remarkValue =
            sch?.remark ?? sch?.remarks ?? sch?.Remark ?? sch?.Remarks ?? ''
          const typeLabel = paperType === 'theory' ? 'Theory' : 'Practical'
          const baseName = sbj.subject_name || `Subject #${csId}`
          return {
            row_key: `${csId}-${paperType}`,
            paper_type: paperType,
            schedule_id: sch?.id != null ? Number(sch.id) : null,
            course_subject_id: csId,
            subject_name: `${baseName} (${typeLabel})`,
            exam_date: sch?.exam_date ? String(sch.exam_date).slice(0, 10) : '',
            start_time: sch ? toTimeInputValue(sch.start_time) : '',
            end_time: sch?.end_time ? toTimeInputValue(sch.end_time) : '',
            remark: remarkValue != null ? String(remarkValue) : '',
          }
        })
      })

      setRows(nextRows)
    } catch (err) {
      console.error(err)
      toast.error(err.response?.data?.message || 'Failed to load exam schedule data')
      setExam(null)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [examid, token])

  useEffect(() => {
    if (canManageExams) loadPage()
  }, [canManageExams, loadPage])

  const updateRow = (rowKey, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.row_key === rowKey ? { ...r, [field]: value } : r)),
    )
  }

  const applyFirstRowToAll = () => {
    if (rows.length === 0) return
    const { exam_date, start_time, end_time } = rows[0]
    setRows((prev) => prev.map((r) => ({ ...r, exam_date, start_time, end_time })))
    toast.success('Applied first row date and times to all subjects')
  }

  const extractSaveError = (err) => {
    let msg = err.response?.data?.message
    const errs = err.response?.data?.errors
    if (!msg && errs && typeof errs === 'object') {
      msg = JSON.stringify(errs)
    }
    return msg || 'Could not save schedules'
  }

  const handleSaveRow = async (row) => {
    if (!exam?.id) return

    const validationError = validateScheduleRow(row)
    if (validationError) {
      toast.error(validationError)
      return
    }

    const headers = buildInstituteHeaders(token)
    if (!headers?.['institute-Id']) {
      toast.error('Select an institute before saving schedules.')
      return
    }

    setSavingRowId(row.row_key)
    try {
      const payload = buildSchedulePayload(row)
      if (row.schedule_id) {
        const res = await axios.put(
          `${BASE_URL}/exam/subject-schedules/${row.schedule_id}`,
          payload,
          { headers },
        )
        toast.success(res.data?.message || `${row.subject_name} schedule updated`)
      } else {
        const res = await axios.post(
          `${BASE_URL}/exam/subject-schedules`,
          {
            exam_id: Number(exam.id),
            schedules: [
              {
                course_subject_id: row.course_subject_id,
                ...payload,
              },
            ],
          },
          { headers },
        )
        toast.success(res.data?.message || `${row.subject_name} schedule added`)
      }
      await loadPage()
    } catch (err) {
      toast.error(extractSaveError(err))
    } finally {
      setSavingRowId(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return

    // Pending / no saved schedule: remove from UI only
    if (!deleteTarget.schedule_id) {
      setRows((prev) => prev.filter((r) => r.row_key !== deleteTarget.row_key))
      setDeleteTarget(null)
      toast.success(`${deleteTarget.subject_name} removed`)
      return
    }

    const headers = buildInstituteHeaders(token)
    if (!headers) {
      toast.error('You need to be signed in to delete schedules.')
      return
    }

    setDeleting(true)
    try {
      const res = await axios.delete(
        `${BASE_URL}/exam/subject-schedules/${deleteTarget.schedule_id}`,
        { headers },
      )
      setRows((prev) => prev.filter((r) => r.row_key !== deleteTarget.row_key))
      toast.success(res.data?.message || 'Schedule deleted successfully')
      setDeleteTarget(null)
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409
          ? "This exam schedule subject can't be deleted, results already generated."
          : 'Could not delete schedule')
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!exam?.id) return

    const headers = buildInstituteHeaders(token)
    if (!headers?.['institute-Id']) {
      toast.error('Select an institute before saving schedules.')
      return
    }

    for (const row of rows) {
      const validationError = validateScheduleRow(row)
      if (validationError) {
        toast.error(validationError)
        return
      }
    }

    const newRows = rows.filter((row) => !row.schedule_id)
    const existingRows = rows.filter((row) => row.schedule_id)

    setSaving(true)
    try {
      const requests = []

      if (newRows.length > 0) {
        requests.push(
          axios.post(
            `${BASE_URL}/exam/subject-schedules`,
            {
              exam_id: Number(exam.id),
              schedules: newRows.map((row) => ({
                course_subject_id: row.course_subject_id,
                ...buildSchedulePayload(row),
              })),
            },
            { headers },
          ),
        )
      }

      for (const row of existingRows) {
        requests.push(
          axios.put(
            `${BASE_URL}/exam/subject-schedules/${row.schedule_id}`,
            buildSchedulePayload(row),
            { headers },
          ),
        )
      }

      await Promise.all(requests)
      toast.success('Exam schedules saved successfully')
      await loadPage()
    } catch (err) {
      toast.error(extractSaveError(err))
    } finally {
      setSaving(false)
    }
  }

  const inputFocus = (e) => {
    e.target.style.borderColor = theme.ink
    e.target.style.boxShadow = '0 0 0 1px rgba(34, 56, 67, 0.15)'
  }
  const inputBlur = (e) => {
    e.target.style.borderColor = theme.border
    e.target.style.boxShadow = 'none'
  }

  if (!canManageExams) {
    return <Navigate to="/ExamSheet" replace />
  }

  const formatExamDate = (d) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return String(d)
    }
  }

  const horizontalPad = isMobile
    ? 'clamp(16px, 5vw, 28px)'
    : isCollapsed
      ? 'clamp(24px, 3vw, 40px)'
      : 'clamp(32px, 4.5vw, 56px)'

  return (
    <>
      <style>
        {`
          .exam-schedule-page {
            color-scheme: light;
          }
          .exam-schedule-page .schedule-table thead th {
            font-size: 13px;
            font-weight: 600;
            padding: 14px 16px;
            border: none;
            border-bottom: 1px solid ${theme.border};
            text-align: left;
            color: ${theme.text};
            font-family: Inter, system-ui, sans-serif;
            background: ${theme.surface};
          }
          .exam-schedule-page .schedule-table tbody td {
            font-size: 14px;
            padding: 14px 16px;
            border: none;
            border-bottom: 1px solid #E5E7EB;
            color: ${theme.text};
            font-family: Inter, system-ui, sans-serif;
            vertical-align: middle;
          }
          .exam-schedule-page .schedule-table tbody tr:hover td {
            background: rgba(37, 99, 235, 0.04);
          }
          .exam-schedule-page .schedule-table .form-control {
            border-radius: 8px;
            border: 1px solid ${theme.border};
            font-size: 14px;
            background-color: ${theme.cardBg};
            color: ${theme.text};
            color-scheme: light;
            caret-color: ${theme.text};
          }
          .exam-schedule-page .schedule-table .form-control::placeholder {
            color: ${theme.muted};
            opacity: 1;
          }
          .exam-schedule-page .schedule-table .form-control:focus {
            background-color: ${theme.cardBg};
            color: ${theme.text};
            color-scheme: light;
          }
          .exam-schedule-page .exam-summary-card-body {
            padding: clamp(1.35rem, 4vw, 2.5rem) clamp(1.25rem, 4.5vw, 3rem);
          }
          .exam-schedule-page .exam-summary-meta {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            align-items: start;
          }
          @media (min-width: 768px) {
            .exam-schedule-page .exam-summary-meta {
              grid-template-columns: minmax(0, 1fr) minmax(300px, 1.05fr);
              column-gap: clamp(1.25rem, 4vw, 2.75rem);
              row-gap: 0;
            }
          }
          .exam-schedule-page .exam-summary-primary {
            min-width: 0;
          }
          .exam-schedule-page .exam-summary-stats {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1rem;
            width: 100%;
            min-width: 0;
            justify-items: start;
          }
          @media (min-width: 480px) {
            .exam-schedule-page .exam-summary-stats {
              grid-template-columns: repeat(3, minmax(0, 1fr));
              column-gap: clamp(1rem, 3.5vw, 2rem);
              row-gap: 0.75rem;
              align-items: start;
              justify-items: start;
            }
          }
          .exam-schedule-page .exam-stat {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            min-width: 0;
          }
          .exam-schedule-page .exam-stat--window,
          .exam-schedule-page .exam-stat--compact {
            min-width: 0;
          }
          .exam-schedule-page .exam-stat-label {
            text-transform: uppercase;
            font-size: 11px;
            font-weight: 600;
            color: ${theme.muted};
            letter-spacing: 0.06em;
            line-height: 1.25;
            white-space: normal;
            max-width: 100%;
          }
          .exam-schedule-page .exam-stat-value {
            font-size: 14px;
            color: ${theme.text};
            font-family: Inter, system-ui, sans-serif;
            line-height: 1.45;
            word-break: break-word;
          }
          .exam-schedule-page .exam-title {
            font-size: 18px;
            font-weight: 700;
            color: ${theme.ink};
            font-family: Inter, system-ui, sans-serif;
            line-height: 1.3;
            word-break: break-word;
          }
          .exam-schedule-page .exam-course {
            font-size: 14px;
            color: ${theme.muted};
            margin-top: 4px;
            line-height: 1.4;
          }
        `}
      </style>
<div
  className="exam-schedule-page"
  style={{
    marginLeft,
    transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: 'margin-left',
    WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 1,
    marginTop: '0px',
    paddingLeft: isCollapsed ? '12px' : '12px',
    paddingRight: isCollapsed ? '12px' : '12px',
  }}
>


        {/* <div
          className="container-fluid p-0"
          style={{
            marginTop: isMobile ? '55px' : '-12px',
            paddingLeft: horizontalPad,
            paddingRight: horizontalPad,
            boxSizing: 'border-box',
          }}
        >
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: theme.surface,
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: `1px solid ${theme.border}`,
                width: '100%',
                borderRadius: isMobile ? 0 : '0 0 10px 10px',
                boxSizing: 'border-box',
              }}
            >
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: theme.ink,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <BsCalendar2Week size={22} style={{ color: theme.ink }} />
                  <div>
                    <h3
                      className="m-0 fw-bold"
                      style={{
                        letterSpacing: '-0.02em',
                        fontSize: 'clamp(18px, 2.5vw, 22px)',
                        color: theme.ink,
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Exam subject schedule
                    </h3>
                    <div
                      className="text-muted small"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px' }}
                    >
                      {instituteName ? `${instituteName} · ` : ''}
                      Set date and time for each subject in one save
                    </div>
                  </div>
                </div>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
        </div> */}
<div
  className="container-fluid p-0"
  style={{
    marginTop: window.innerWidth < 992 ? '55px' : '-12px',
    paddingLeft: '0px',
    paddingRight: '0px',
  }}
>
  <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>  {/* ← ADD THIS */}
    <div
      className="d-flex justify-content-between align-items-center"
      style={{
        background: '#F3F4F6',
        paddingLeft: '28px',
        paddingRight: '28px',
        paddingTop: '12px',
        paddingBottom: '12px',
        overflow: 'visible',
        borderBottom: '1px solid #D1D3D4',
        width: '100%',
      }}
    >    {/* Left */}
    <div className="d-flex align-items-center gap-3">
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          overflow: 'hidden',
          background: '#223843',
          display: 'flex',
          alignItems: 'right',
          justifyContent: 'center',
        }}
      >
        {!logoError ? (
          <img
            src={logo}
            alt="School Logo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => setLogoError(true)}
          />
        ) : (
          <LuGraduationCap
            size={20}
            style={{ color: '#fff' }}
          />
        )}
      </div>

      <div>
        <h3
          className="m-0 fw-bold"
          style={{
            fontSize: '22px',
            color: '#223843',
            fontFamily: 'Inter, system-ui, sans-serif',
            textAlign:'left'
          }}
        >
          Exam Subject Schedule
        </h3>

        <p
          className="m-0 mt-1"
          style={{
            fontSize: '13px',
            color: '#6b7280',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          {instituteName
            ? `${instituteName} · `
            : ''}
          Manage subject exam dates & timings
        </p>
      </div>
    </div>

    {/* Right */}
    {!isMobile && (
      <ProfileDropdown logout={logout} />
    )}
  </div>
  </div>
</div>


        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '60vh',
            paddingLeft: horizontalPad,
            paddingRight: horizontalPad,
            paddingBottom: '48px',
            boxSizing: 'border-box',
            maxWidth: '100%',
          }}
        >
          {/* <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"> */}


{exam && (
  <div style={{
    background: '#ffffff',
    border: '1px solid #D1D3D4',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '24px',
    fontFamily: 'Inter, system-ui, sans-serif',
    width: '100%',
  }}>


    {/* Dark top banner */}
    <div style={{
      background: '#223843',
      padding: '20px 28px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    }}>
      {/* Top row: Back btn left, Status pill right */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <button
          type="button"
          onClick={() => navigate('/ExamSheet')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.1)',
            border: '0.5px solid rgba(255,255,255,0.25)',
            borderRadius: '8px',
            padding: '7px 14px',
            fontSize: '13px',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontFamily: 'Inter, system-ui, sans-serif',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <FaArrowLeft size={13} />
          Back to exams
        </button>

        {/* Status pill */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 14px',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 500,
          background: exam.status === 'cancel' ? 'rgba(239,68,68,0.18)' : 'rgba(93,202,165,0.18)',
          border: `0.5px solid ${exam.status === 'cancel' ? 'rgba(239,68,68,0.4)' : 'rgba(93,202,165,0.4)'}`,
          color: exam.status === 'cancel' ? '#F09595' : '#5DCAA5',
          textTransform: 'capitalize',
        }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: '50%',
            background: exam.status === 'cancel' ? '#F09595' : '#5DCAA5',
          }} />
          {exam.status || '—'}
        </div>
      </div>

      {/* Exam name + badge row */}
      <div>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(255,255,255,0.1)',
          border: '0.5px solid rgba(255,255,255,0.2)',
          borderRadius: '999px',
          padding: '4px 12px',
          fontSize: '12px',
          color: 'rgba(255,255,255,0.75)',
          fontWeight: 500,
          marginBottom: '10px',
        }}>
          Exam #{exam.id}&nbsp;·&nbsp;Session {exam.session}
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 600, color: '#fff', margin: '0 0 4px', lineHeight: 1.3 }}>
          {exam.exam_name}
        </h2>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', margin: 0 }}>
          {courseName || '—'}
        </p>
      </div>
    </div>

    {/* Stats row */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
    }}>

      <div style={{
        padding: '20px 24px',
        borderRight: isMobile ? 'none' : '1px solid #D1D3D4',
        borderBottom: isMobile ? '1px solid #D1D3D4' : 'none',
      }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '8px',
          background: '#F3F4F6', display: 'flex', alignItems: 'center',
          justifyContent: 'center', marginBottom: '8px',
        }}>
          <BsCalendar2Week size={16} style={{ color: '#223843' }} />
        </div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '4px' }}>
          Exam Window
        </div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827', lineHeight: 1.5 }}>
          {formatExamDate(exam.start_date)}
          <span style={{ color: '#9ca3af', margin: '0 4px' }}>→</span>
          {formatExamDate(exam.end_date)}
        </div>
      </div>

      <div style={{
        padding: '20px 24px',
        borderRight: isMobile ? 'none' : '1px solid #D1D3D4',
        borderBottom: isMobile ? '1px solid #D1D3D4' : 'none',
      }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '8px',
          background: '#E1F5EE', display: 'flex', alignItems: 'center',
          justifyContent: 'center', marginBottom: '8px',
        }}>
          <BsJournalText size={16} style={{ color: '#0F6E56' }} />
        </div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '4px' }}>
          Total marks / Subject
        </div>
        <div style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }}>
          {exam.total_marks ?? '—'}
        </div>

        <div
  style={{
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '6px',
    lineHeight: '1.5',
    fontWeight: 500,
  }}
>
  {rows.map((row) => row.subject_name).join(', ')}
</div>
        
      </div>

      <div style={{ padding: '20px 24px' }}>
       
       
        <div style={{
          width: '32px', height: '32px', borderRadius: '8px',
          background: '#E6F1FB', display: 'flex', alignItems: 'center',
          justifyContent: 'center', marginBottom: '8px',
        }}>
          <BsJournalText size={16} style={{ color: '#185FA5' }} />
        </div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '4px' }}>
          Subjects
        </div>
        <div style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }}>
          {rows.length}
        </div>
        {/* <div style={{ fontSize: '12px', color: '#6b7280' }}>All scheduled</div> */}
      </div>
    </div>
  </div>
)}

{/* If exam is null, still show Back button standalone */}
{!exam && !loading && (
  <div className="mb-4">
    <Button
      variant="light"
      className="d-flex align-items-center gap-2 border"
      onClick={() => navigate('/ExamSheet')}
      style={{ borderRadius: '10px', borderColor: '#D1D3D4', color: '#111827', fontWeight: 500, fontFamily: 'Inter, system-ui, sans-serif', padding: '10px 16px' }}
    >
      <FaArrowLeft /> Back to exams
    </Button>
  </div>
)}
          {/* </div> */}

          {loading ? (
            <Card
              className="border shadow-sm text-center py-5"
              style={{ borderRadius: '12px', borderColor: theme.border }}
            >
              <Spinner animation="border" variant="primary" style={{ color: theme.ink }} />
              <p className="text-muted mt-3 mb-0" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                Loading exam and subjects…
              </p>
            </Card>
          ) : !exam ? (
            <Card
              className="border shadow-sm"
              style={{ borderRadius: '12px', borderColor: theme.border, maxWidth: '520px' }}
            >
              <Card.Body className="p-4">
                <h5 className="fw-semibold mb-2" style={{ color: theme.ink, fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Exam not found
                </h5>
                <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                  This link may be invalid or the exam was removed.
                </p>
                <Button
                  onClick={() => navigate('/ExamSheet')}
                  style={{
                    background: theme.ink,
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                  }}
                >
                  Return to exams
                </Button>
              </Card.Body>
            </Card>
          ) : rows.length === 0 ? (
            <Card
              className="border shadow-sm"
              style={{ borderRadius: '12px', borderColor: theme.border, maxWidth: '640px' }}
            >
              <Card.Body className="p-4 d-flex gap-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 48, height: 48, background: theme.surface, color: theme.ink }}
                >
                  <BsJournalText size={22} />
                </div>
                <div>
                  <h5 className="fw-semibold mb-2" style={{ color: theme.ink, fontFamily: 'Inter, system-ui, sans-serif' }}>
                    No subjects on this course
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    Map subjects to the course first, then you can set per-subject exam dates and times here.
                  </p>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Card
                className="border shadow-sm mb-4 overflow-hidden"
                style={{ borderRadius: '12px', borderColor: theme.border }}
              >
                <Card.Header
                  className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 px-4"
                  style={{ borderBottom: `1px solid ${theme.border}` }}
                >
                  <div>
                    <div className="fw-semibold" style={{ color: theme.ink, fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px' }}>
                      Subject timetable
                    </div>
                    <div className="text-muted small mt-1" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {rows.length} subject{rows.length !== 1 ? 's' : ''} ·{' '}
                      {rows.filter((r) => r.schedule_id).length} saved ·{' '}
                      {rows.filter((r) => !r.schedule_id).length} pending
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={applyFirstRowToAll}
                    className="d-flex align-items-center gap-2"
                    style={{
                      borderRadius: '8px',
                      borderColor: theme.border,
                      fontWeight: 500,
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    <BsClock size={16} />
                    Copy first row to all
                  </Button>
                </Card.Header>
                <div className="table-responsive">
                  <table className="table mb-0 schedule-table">
                    <thead>
                      <tr>
                        <th style={{ width: '56px', minWidth: '56px' }}>#</th>
                        <th style={{ minWidth: '200px' }}>Subject</th>
                        <th style={{ minWidth: '160px' }}>Exam date</th>
                        <th style={{ minWidth: '140px' }}>Start</th>
                        <th style={{ minWidth: '140px' }}>End (optional)</th>
                        <th style={{ minWidth: '120px' }}>Status</th>
                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const isRowSaving = savingRowId === row.row_key
                        return (
                        <tr key={row.row_key}>
                          <td className="text-muted fw-medium">{index + 1}</td>
                          <td className="fw-semibold">{row.subject_name}</td>
                          <td>
                            <Form.Control
                              type="date"
                              value={row.exam_date}
                              onChange={(e) =>
                                updateRow(row.row_key, 'exam_date', e.target.value)
                              }
                              onFocus={inputFocus}
                              onBlur={inputBlur}
                              required
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="time"
                              value={row.start_time}
                              onChange={(e) =>
                                updateRow(row.row_key, 'start_time', e.target.value)
                              }
                              onFocus={inputFocus}
                              onBlur={inputBlur}
                              required
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="time"
                              value={row.end_time}
                              onChange={(e) =>
                                updateRow(row.row_key, 'end_time', e.target.value)
                              }
                              onFocus={inputFocus}
                              onBlur={inputBlur}
                            />
                          </td>
                          <td>
                            {row.schedule_id ? (
                              <Badge
                                bg="success"
                                className="fw-normal"
                                style={{ fontSize: '12px', padding: '6px 10px' }}
                              >
                                Saved
                              </Badge>
                            ) : (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="fw-normal"
                                style={{ fontSize: '12px', padding: '6px 10px' }}
                              >
                                Not added
                              </Badge>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="d-inline-flex align-items-center gap-2">
                              <Button
                                type="button"
                                variant={row.schedule_id ? 'outline-primary' : 'primary'}
                                size="sm"
                                disabled={saving || isRowSaving || deleting}
                                onClick={() => handleSaveRow(row)}
                                className="d-inline-flex align-items-center justify-content-center"
                                title={
                                  row.schedule_id
                                    ? 'Update Subject Schedule'
                                    : 'Add Subject Schedule'
                                }
                                aria-label={
                                  row.schedule_id
                                    ? 'Update Subject Schedule'
                                    : 'Add Subject Schedule'
                                }
                                style={{
                                  borderRadius: '8px',
                                  fontWeight: 500,
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  width: '36px',
                                  height: '32px',
                                  padding: 0,
                                  ...(row.schedule_id
                                    ? {}
                                    : { background: theme.ink, borderColor: theme.ink }),
                                }}
                              >
                                {isRowSaving ? (
                                  <Spinner animation="border" size="sm" />
                                ) : row.schedule_id ? (
                                  <BsPencilFill size={14} />
                                ) : (
                                  <BsPlusLg size={14} />
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                disabled={saving || isRowSaving || deleting}
                                onClick={() => setDeleteTarget(row)}
                                className="d-inline-flex align-items-center justify-content-center"
                                title="Remove Schedule Subject"
                                aria-label="Remove Schedule Subject"
                                style={{
                                  borderRadius: '8px',
                                  width: '36px',
                                  height: '32px',
                                  padding: 0,
                                }}
                              >
                                <BsTrashFill size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div className="d-flex flex-wrap gap-2 justify-content-end pt-2">
                <Button
                  variant="light"
                  type="button"
                  onClick={() => navigate('/ExamSheet')}
                  className="border"
                  style={{
                    borderRadius: '8px',
                    borderColor: theme.border,
                    minWidth: '120px',
                    fontWeight: 600,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  style={{
                    background: theme.ink,
                    border: 'none',
                    borderRadius: '8px',
                    minWidth: '180px',
                    fontWeight: 600,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    'Save all schedules'
                  )}
                </Button>
              </div>
              <p
                className="text-muted small text-end mb-0 mt-2"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                New subjects use POST · existing schedules use PUT update
              </p>
            </Form>
          )}
        </div>
      </div>

      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => !deleting && setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            Remove Schedule Subject
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
          Remove <strong>{deleteTarget?.subject_name}</strong> from this exam schedule?
          {deleteTarget?.schedule_id
            ? ' This will permanently delete the saved schedule.'
            : ' This subject will be removed from the list.'}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Removing…
              </>
            ) : (
              'Remove'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}


















// import React, { useCallback, useEffect, useMemo, useState } from 'react'
// import {
//   Badge,
//   Button,
//   Card,
//   Form,
//   Spinner,
// } from 'react-bootstrap'
// import { Navigate, useNavigate, useParams } from 'react-router-dom'
// import toast from 'react-hot-toast'
// import { FaArrowLeft } from 'react-icons/fa'
// import { BsCalendar2Week, BsClock, BsJournalText } from 'react-icons/bs'
// import axios from 'axios'
// import { BASE_URL } from './commonComponents/ApiEndpoints'
// import Cookies from 'js-cookie'
// import { useAuth } from './AuthContext'
// import { useSidebar } from './SidebarContext'
// import { useInstitute } from './commonComponents/InstituteContext'
// import ProfileDropdown from './commonComponents/ProfileDropdown'
// import { LuGraduationCap } from 'react-icons/lu'

// function toTimeInputValue(value) {
//   if (value == null || value === '') return ''
//   const s = String(value).trim()
//   if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5)
//   if (/^\d{2}:\d{2}$/.test(s)) return s
//   return s.length >= 5 ? s.slice(0, 5) : s
// }

// function timeToMinutes(t) {
//   const parts = String(t).trim().split(':')
//   if (parts.length < 2) return NaN
//   const h = Number(parts[0])
//   const m = Number(parts[1])
//   if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN
//   return h * 60 + m
// }

// function resolveCanManageExams(user) {
//   if (!user) return false
//   const t = (user.user_type || '').toLowerCase()
//   const roles = (user.roles || []).map((r) => String(r).toLowerCase())
//   return (
//     t === 'admin' ||
//     roles.includes('admin') ||
//     t === 'teacher' ||
//     roles.includes('teacher') ||
//     t === 'superadmin' ||
//     roles.includes('superadmin') ||
//     t === 'manage_exams' ||
//     roles.includes('manage_exams')
//   )
// }

// function buildInstituteHeaders(token) {
//   if (!token) return null
//   const raw = Cookies.get('institute_id')
//   const id = raw != null && raw !== '' ? Number(raw) : NaN
//   const headers = {
//     Authorization: `Bearer ${token}`,
//     'Content-Type': 'application/json',
//     Accept: 'application/json',
//   }
//   if (Number.isFinite(id)) {
//     headers['institute-Id'] = id
//   }
//   return headers
// }

// const theme = {
//   ink: '#223843',
//   border: '#D1D3D4',
//   muted: '#6b7280',
//   surface: '#F3F4F6',
//   text: '#111827',
//   cardBg: '#ffffff',
// }

// export default function ExamSubjectSchedule() {
//   const { examid } = useParams()
//   const navigate = useNavigate()
//   const { user, logout, token } = useAuth()
//   const { isCollapsed } = useSidebar()
//   const { logo, name: instituteName } = useInstitute()
//   const [logoError, setLogoError] = useState(false)
//   const [marginLeft, setMarginLeft] = useState('0px')
//   const [isMobile, setIsMobile] = useState(
//     typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
//   )

//   const [loading, setLoading] = useState(true)
//   const [saving, setSaving] = useState(false)
//   const [exam, setExam] = useState(null)
//   const [courseName, setCourseName] = useState('')
//   const [rows, setRows] = useState([])
//   const [hadExistingSchedules, setHadExistingSchedules] = useState(false)

//   const canManageExams = useMemo(() => resolveCanManageExams(user), [user])

//   useEffect(() => {
//     const handleResize = () => {
//       if (window.innerWidth >= 992) {
//         const expandedWidth = 245
//         const collapsedWidth = 85
//         const gap = 1
//         setMarginLeft(
//           isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
//         )
//       } else {
//         setMarginLeft('0px')
//       }
//       setIsMobile(window.innerWidth <= 768)
//     }
//     handleResize()
//     window.addEventListener('resize', handleResize)
//     return () => window.removeEventListener('resize', handleResize)
//   }, [isCollapsed])

//   const loadPage = useCallback(async () => {
//     const idNum = Number(examid)
//     if (!examid || !Number.isFinite(idNum)) {
//       setExam(null)
//       setRows([])
//       setLoading(false)
//       return
//     }

//     const headers = buildInstituteHeaders(token)
//     if (!headers) {
//       toast.error('You need to be signed in to load schedules.')
//       setLoading(false)
//       return
//     }

//     setLoading(true)
//     try {
//       const [coursesRes, examsRes, schedulesRes] = await Promise.all([
//         axios.get(`${BASE_URL}/course/all`, { headers }),
//         axios.get(`${BASE_URL}/get/exam/all`, { headers }),
//         axios
//           .get(`${BASE_URL}/exam/subject-schedules`, {
//             headers,
//             params: { exam_id: idNum },
//           })
//           .catch(() => ({ data: { data: [] } })),
//       ])

//       const courses = coursesRes.data?.data || []
//       const exams = examsRes.data?.data || []
//       const found = exams.find((e) => Number(e.id) === idNum) || null
//       setExam(found)

//       if (found?.course_id) {
//         const cn = courses.find((c) => Number(c.id) === Number(found.course_id))?.name || ''
//         setCourseName(cn)
//       } else {
//         setCourseName('')
//       }

//       const schedulePayload = schedulesRes.data?.data ?? schedulesRes.data ?? []
//       const list = Array.isArray(schedulePayload) ? schedulePayload : []
//       setHadExistingSchedules(list.length > 0)

//       if (!found?.course_id) {
//         setRows([])
//         setLoading(false)
//         return
//       }

//       const subRes = await axios.get(
//         `${BASE_URL}/get/subjects/by-course?course_id=${encodeURIComponent(String(found.course_id))}`,
//         { headers },
//       )
//       const subjects = subRes.data?.data || []

//       const byCourseSubjectId = new Map()
//       list.forEach((sch) => {
//         const csId = sch.course_subject_id ?? sch.course_subject?.id
//         if (csId != null) byCourseSubjectId.set(Number(csId), sch)
//       })

//       const nextRows = subjects.map((sbj) => {
//         const csId = Number(sbj.id)
//         const sch = byCourseSubjectId.get(csId)
//         return {
//           course_subject_id: csId,
//           subject_name: sbj.subject_name || `Subject #${csId}`,
//           exam_date: sch?.exam_date ? String(sch.exam_date).slice(0, 10) : '',
//           start_time: sch ? toTimeInputValue(sch.start_time) : '',
//           end_time: sch?.end_time ? toTimeInputValue(sch.end_time) : '',
//         }
//       })

//       setRows(nextRows)
//     } catch (err) {
//       console.error(err)
//       toast.error(err.response?.data?.message || 'Failed to load exam schedule data')
//       setExam(null)
//       setRows([])
//     } finally {
//       setLoading(false)
//     }
//   }, [examid, token])

//   useEffect(() => {
//     if (canManageExams) loadPage()
//   }, [canManageExams, loadPage])

//   const updateRow = (courseSubjectId, field, value) => {
//     setRows((prev) =>
//       prev.map((r) =>
//         r.course_subject_id === courseSubjectId ? { ...r, [field]: value } : r,
//       ),
//     )
//   }

//   const applyFirstRowToAll = () => {
//     if (rows.length === 0) return
//     const { exam_date, start_time, end_time } = rows[0]
//     setRows((prev) => prev.map((r) => ({ ...r, exam_date, start_time, end_time })))
//     toast.success('Applied first row date and times to all subjects')
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     if (!exam?.id) return

//     const headers = buildInstituteHeaders(token)
//     if (!headers?.['institute-Id']) {
//       toast.error('Select an institute before saving schedules.')
//       return
//     }

//     for (const row of rows) {
//       if (!row.exam_date?.trim() || !row.start_time?.trim()) {
//         toast.error(
//           `Each subject needs an exam date and start time (${row.subject_name} is incomplete).`,
//         )
//         return
//       }
//       if (row.end_time?.trim()) {
//         const a = timeToMinutes(row.start_time)
//         const b = timeToMinutes(row.end_time)
//         if (Number.isFinite(a) && Number.isFinite(b) && a > b) {
//           toast.error(`Start time must be before end time for ${row.subject_name}`)
//           return
//         }
//       }
//     }

//     const schedules = rows.map((row) => {
//       const entry = {
//         course_subject_id: row.course_subject_id,
//         exam_date: row.exam_date.trim(),
//         start_time: row.start_time.trim(),
//       }
//       if (row.end_time?.trim()) entry.end_time = row.end_time.trim()
//       return entry
//     })

//     setSaving(true)
//     try {
//       const res = await axios.post(
//         `${BASE_URL}/exam/subject-schedules`,
//         {
//           exam_id: Number(exam.id),
//           schedules,
//         },
//         { headers },
//       )
//       const msg = res.data?.message || 'Exam schedules saved successfully'
//       toast.success(msg)
//       await loadPage()
//     } catch (err) {
//       let msg = err.response?.data?.message
//       const errs = err.response?.data?.errors
//       if (!msg && errs && typeof errs === 'object') {
//         msg = JSON.stringify(errs)
//       }
//       toast.error(msg || 'Could not save schedules')
//     } finally {
//       setSaving(false)
//     }
//   }

//   const inputFocus = (e) => {
//     e.target.style.borderColor = theme.ink
//     e.target.style.boxShadow = '0 0 0 1px rgba(34, 56, 67, 0.15)'
//   }
//   const inputBlur = (e) => {
//     e.target.style.borderColor = theme.border
//     e.target.style.boxShadow = 'none'
//   }

//   if (!canManageExams) {
//     return <Navigate to="/ExamSheet" replace />
//   }

//   const formatExamDate = (d) => {
//     if (!d) return '—'
//     try {
//       return new Date(d).toLocaleDateString('en-GB', {
//         day: '2-digit',
//         month: 'short',
//         year: 'numeric',
//       })
//     } catch {
//       return String(d)
//     }
//   }

//   const horizontalPad = isMobile
//     ? 'clamp(16px, 5vw, 28px)'
//     : isCollapsed
//       ? 'clamp(24px, 3vw, 40px)'
//       : 'clamp(32px, 4.5vw, 56px)'

//   return (
//     <>
//       <style>
//         {`
//           .exam-schedule-page {
//             color-scheme: light;
//           }
//           .exam-schedule-page .schedule-table thead th {
//             font-size: 13px;
//             font-weight: 600;
//             padding: 14px 16px;
//             border: none;
//             border-bottom: 1px solid ${theme.border};
//             text-align: left;
//             color: ${theme.text};
//             font-family: Inter, system-ui, sans-serif;
//             background: ${theme.surface};
//           }
//           .exam-schedule-page .schedule-table tbody td {
//             font-size: 14px;
//             padding: 14px 16px;
//             border: none;
//             border-bottom: 1px solid #E5E7EB;
//             color: ${theme.text};
//             font-family: Inter, system-ui, sans-serif;
//             vertical-align: middle;
//           }
//           .exam-schedule-page .schedule-table tbody tr:hover td {
//             background: rgba(37, 99, 235, 0.04);
//           }
//           .exam-schedule-page .schedule-table .form-control {
//             border-radius: 8px;
//             border: 1px solid ${theme.border};
//             font-size: 14px;
//             background-color: ${theme.cardBg};
//             color: ${theme.text};
//             color-scheme: light;
//             caret-color: ${theme.text};
//           }
//           .exam-schedule-page .schedule-table .form-control::placeholder {
//             color: ${theme.muted};
//             opacity: 1;
//           }
//           .exam-schedule-page .schedule-table .form-control:focus {
//             background-color: ${theme.cardBg};
//             color: ${theme.text};
//             color-scheme: light;
//           }
//           .exam-schedule-page .exam-summary-card-body {
//             padding: clamp(1.35rem, 4vw, 2.5rem) clamp(1.25rem, 4.5vw, 3rem);
//           }
//           .exam-schedule-page .exam-summary-meta {
//             display: grid;
//             grid-template-columns: 1fr;
//             gap: 1.5rem;
//             align-items: start;
//           }
//           @media (min-width: 768px) {
//             .exam-schedule-page .exam-summary-meta {
//               grid-template-columns: minmax(0, 1fr) minmax(300px, 1.05fr);
//               column-gap: clamp(1.25rem, 4vw, 2.75rem);
//               row-gap: 0;
//             }
//           }
//           .exam-schedule-page .exam-summary-primary {
//             min-width: 0;
//           }
//           .exam-schedule-page .exam-summary-stats {
//             display: grid;
//             grid-template-columns: 1fr;
//             gap: 1rem;
//             width: 100%;
//             min-width: 0;
//             justify-items: start;
//           }
//           @media (min-width: 480px) {
//             .exam-schedule-page .exam-summary-stats {
//               grid-template-columns: repeat(3, minmax(0, 1fr));
//               column-gap: clamp(1rem, 3.5vw, 2rem);
//               row-gap: 0.75rem;
//               align-items: start;
//               justify-items: start;
//             }
//           }
//           .exam-schedule-page .exam-stat {
//             display: flex;
//             flex-direction: column;
//             gap: 0.5rem;
//             min-width: 0;
//           }
//           .exam-schedule-page .exam-stat--window,
//           .exam-schedule-page .exam-stat--compact {
//             min-width: 0;
//           }
//           .exam-schedule-page .exam-stat-label {
//             text-transform: uppercase;
//             font-size: 11px;
//             font-weight: 600;
//             color: ${theme.muted};
//             letter-spacing: 0.06em;
//             line-height: 1.25;
//             white-space: normal;
//             max-width: 100%;
//           }
//           .exam-schedule-page .exam-stat-value {
//             font-size: 14px;
//             color: ${theme.text};
//             font-family: Inter, system-ui, sans-serif;
//             line-height: 1.45;
//             word-break: break-word;
//           }
//           .exam-schedule-page .exam-title {
//             font-size: 18px;
//             font-weight: 700;
//             color: ${theme.ink};
//             font-family: Inter, system-ui, sans-serif;
//             line-height: 1.3;
//             word-break: break-word;
//           }
//           .exam-schedule-page .exam-course {
//             font-size: 14px;
//             color: ${theme.muted};
//             margin-top: 4px;
//             line-height: 1.4;
//           }
//         `}
//       </style>
//       <div
//         className="exam-schedule-page"
//         style={{
//           marginLeft,
//           transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
//           position: 'relative',
//           zIndex: 1,
//           maxWidth: '100%',
//           overflowX: 'hidden',
//           boxSizing: 'border-box',
//         }}
//       >
//         <div
//           className="container-fluid p-0"
//           style={{
//             marginTop: isMobile ? '55px' : '-12px',
//             paddingLeft: horizontalPad,
//             paddingRight: horizontalPad,
//             boxSizing: 'border-box',
//           }}
//         >
//             <div
//               className="d-flex justify-content-between align-items-center"
//               style={{
//                 background: theme.surface,
//                 paddingLeft: '16px',
//                 paddingRight: '16px',
//                 paddingTop: '12px',
//                 paddingBottom: '12px',
//                 borderBottom: `1px solid ${theme.border}`,
//                 width: '100%',
//                 borderRadius: isMobile ? 0 : '0 0 10px 10px',
//                 boxSizing: 'border-box',
//               }}
//             >
//               <div className="d-flex align-items-center gap-3 flex-wrap">
//                 <div
//                   style={{
//                     width: '40px',
//                     height: '40px',
//                     borderRadius: '50%',
//                     overflow: 'hidden',
//                     background: theme.ink,
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     flexShrink: 0,
//                   }}
//                 >
//                   {!logoError ? (
//                     <img
//                       src={logo}
//                       alt=""
//                       style={{ width: '100%', height: '100%', objectFit: 'cover' }}
//                       onError={() => setLogoError(true)}
//                     />
//                   ) : (
//                     <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
//                   )}
//                 </div>
//                 <div className="d-flex align-items-center gap-2 flex-wrap">
//                   <BsCalendar2Week size={22} style={{ color: theme.ink }} />
//                   <div>
//                     <h3
//                       className="m-0 fw-bold"
//                       style={{
//                         letterSpacing: '-0.02em',
//                         fontSize: 'clamp(18px, 2.5vw, 22px)',
//                         color: theme.ink,
//                         fontFamily: 'Inter, system-ui, sans-serif',
//                       }}
//                     >
//                       Exam subject schedule
//                     </h3>
//                     <div
//                       className="text-muted small"
//                       style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px' }}
//                     >
//                       {instituteName ? `${instituteName} · ` : ''}
//                       Set date and time for each subject in one save
//                     </div>
//                   </div>
//                 </div>
//               </div>
//               {!isMobile && <ProfileDropdown logout={logout} />}
//             </div>
//         </div>

//         <div
//           className="container-fluid p-0 mt-4"
//           style={{
//             minHeight: '60vh',
//             paddingLeft: horizontalPad,
//             paddingRight: horizontalPad,
//             paddingBottom: '48px',
//             boxSizing: 'border-box',
//             maxWidth: '100%',
//           }}
//         >
//           <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
//             <Button
//               variant="light"
//               className="d-flex align-items-center gap-2 border"
//               onClick={() => navigate('/ExamSheet')}
//               style={{
//                 borderRadius: '10px',
//                 borderColor: theme.border,
//                 color: theme.text,
//                 fontWeight: 500,
//                 fontFamily: 'Inter, system-ui, sans-serif',
//                 padding: '10px 16px',
//               }}
//             >
//               <FaArrowLeft /> Back to exams
//             </Button>
//             {exam && (
//               <div className="d-flex flex-wrap gap-2 align-items-center">
//                 <Badge
//                   bg="light"
//                   text="dark"
//                   className="border fw-medium"
//                   style={{ borderColor: theme.border, fontSize: '12px', padding: '6px 10px' }}
//                 >
//                   Exam #{exam.id}
//                 </Badge>
//                 {exam.session && (
//                   <Badge
//                     bg="light"
//                     text="dark"
//                     className="border fw-medium"
//                     style={{ borderColor: theme.border, fontSize: '12px', padding: '6px 10px' }}
//                   >
//                     Session {exam.session}
//                   </Badge>
//                 )}
//               </div>
//             )}
//           </div>

//           {loading ? (
//             <Card
//               className="border shadow-sm text-center py-5"
//               style={{ borderRadius: '12px', borderColor: theme.border }}
//             >
//               <Spinner animation="border" variant="primary" style={{ color: theme.ink }} />
//               <p className="text-muted mt-3 mb-0" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
//                 Loading exam and subjects…
//               </p>
//             </Card>
//           ) : !exam ? (
//             <Card
//               className="border shadow-sm"
//               style={{ borderRadius: '12px', borderColor: theme.border, maxWidth: '520px' }}
//             >
//               <Card.Body className="p-4">
//                 <h5 className="fw-semibold mb-2" style={{ color: theme.ink, fontFamily: 'Inter, system-ui, sans-serif' }}>
//                   Exam not found
//                 </h5>
//                 <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
//                   This link may be invalid or the exam was removed.
//                 </p>
//                 <Button
//                   onClick={() => navigate('/ExamSheet')}
//                   style={{
//                     background: theme.ink,
//                     border: 'none',
//                     borderRadius: '8px',
//                     fontWeight: 600,
//                   }}
//                 >
//                   Return to exams
//                 </Button>
//               </Card.Body>
//             </Card>
//           ) : rows.length === 0 ? (
//             <Card
//               className="border shadow-sm"
//               style={{ borderRadius: '12px', borderColor: theme.border, maxWidth: '640px' }}
//             >
//               <Card.Body className="p-4 d-flex gap-3">
//                 <div
//                   className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
//                   style={{ width: 48, height: 48, background: theme.surface, color: theme.ink }}
//                 >
//                   <BsJournalText size={22} />
//                 </div>
//                 <div>
//                   <h5 className="fw-semibold mb-2" style={{ color: theme.ink, fontFamily: 'Inter, system-ui, sans-serif' }}>
//                     No subjects on this course
//                   </h5>
//                   <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
//                     Map subjects to the course first, then you can set per-subject exam dates and times here.
//                   </p>
//                 </div>
//               </Card.Body>
//             </Card>
//           ) : (
//             <Form onSubmit={handleSubmit}>
//               {exam && (
//                 <Card
//                   className="border-0 shadow-sm mb-4 exam-summary-card"
//                   style={{ borderRadius: '12px', background: theme.cardBg, border: `1px solid ${theme.border}` }}
//                 >
//                   <Card.Body className="exam-summary-card-body">
//                     <div className="exam-summary-meta">
//                       <div className="exam-summary-primary">
//                         <div className="exam-stat-label">Exam</div>
//                         <div className="exam-title">{exam.exam_name}</div>
//                         <div className="exam-course">{courseName || 'Course'}</div>
//                       </div>
//                       <div className="exam-summary-stats">
//                         <div className="exam-stat exam-stat--window">
//                           <div className="exam-stat-label">Window</div>
//                           <div className="exam-stat-value">
//                             {formatExamDate(exam.start_date)}
//                             <span className="text-muted px-1">→</span>
//                             {formatExamDate(exam.end_date)}
//                           </div>
//                         </div>
//                         <div className="exam-stat exam-stat--compact">
//                           <div className="exam-stat-label">Total marks</div>
//                           <div className="exam-stat-value fw-semibold">{exam.total_marks ?? '—'}</div>
//                         </div>
//                         <div className="exam-stat exam-stat--compact">
//                           <div className="exam-stat-label">Status</div>
//                           <div>
//                             <Badge
//                               bg={
//                                 exam.status === 'complete'
//                                   ? 'success'
//                                   : exam.status === 'cancel'
//                                     ? 'danger'
//                                     : 'secondary'
//                               }
//                               className="text-capitalize"
//                               style={{ fontWeight: 600 }}
//                             >
//                               {exam.status || '—'}
//                             </Badge>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </Card.Body>
//                 </Card>
//               )}

//               {hadExistingSchedules && (
//                 <div
//                   className="mb-4 px-3 py-3 rounded-3 border"
//                   style={{
//                     background: '#FFFBEB',
//                     borderColor: '#FCD34D',
//                     fontSize: '14px',
//                     color: '#92400E',
//                     fontFamily: 'Inter, system-ui, sans-serif',
//                   }}
//                 >
//                   <strong>Note:</strong> This exam already has saved schedules. Submitting again runs a new batch create
//                   on the server—confirm your backend allows updates or clears old rows if needed.
//                 </div>
//               )}

//               <Card
//                 className="border shadow-sm mb-4 overflow-hidden"
//                 style={{ borderRadius: '12px', borderColor: theme.border }}
//               >
//                 <Card.Header
//                   className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 px-4"
//                   style={{ borderBottom: `1px solid ${theme.border}` }}
//                 >
//                   <div>
//                     <div className="fw-semibold" style={{ color: theme.ink, fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px' }}>
//                       Subject timetable
//                     </div>
//                     <div className="text-muted small mt-1" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
//                       {rows.length} subject{rows.length !== 1 ? 's' : ''} · all rows saved in one request
//                     </div>
//                   </div>
//                   <Button
//                     type="button"
//                     variant="outline-secondary"
//                     size="sm"
//                     onClick={applyFirstRowToAll}
//                     className="d-flex align-items-center gap-2"
//                     style={{
//                       borderRadius: '8px',
//                       borderColor: theme.border,
//                       fontWeight: 500,
//                       fontFamily: 'Inter, system-ui, sans-serif',
//                     }}
//                   >
//                     <BsClock size={16} />
//                     Copy first row to all
//                   </Button>
//                 </Card.Header>
//                 <div className="table-responsive">
//                   <table className="table mb-0 schedule-table">
//                     <thead>
//                       <tr>
//                         <th style={{ width: '56px', minWidth: '56px' }}>#</th>
//                         <th style={{ minWidth: '200px' }}>Subject</th>
//                         <th style={{ minWidth: '160px' }}>Exam date</th>
//                         <th style={{ minWidth: '140px' }}>Start</th>
//                         <th style={{ minWidth: '140px' }}>End (optional)</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {rows.map((row, index) => (
//                         <tr key={row.course_subject_id}>
//                           <td className="text-muted fw-medium">{index + 1}</td>
//                           <td className="fw-semibold">{row.subject_name}</td>
//                           <td>
//                             <Form.Control
//                               type="date"
//                               value={row.exam_date}
//                               onChange={(e) =>
//                                 updateRow(row.course_subject_id, 'exam_date', e.target.value)
//                               }
//                               onFocus={inputFocus}
//                               onBlur={inputBlur}
//                               required
//                             />
//                           </td>
//                           <td>
//                             <Form.Control
//                               type="time"
//                               value={row.start_time}
//                               onChange={(e) =>
//                                 updateRow(row.course_subject_id, 'start_time', e.target.value)
//                               }
//                               onFocus={inputFocus}
//                               onBlur={inputBlur}
//                               required
//                             />
//                           </td>
//                           <td>
//                             <Form.Control
//                               type="time"
//                               value={row.end_time}
//                               onChange={(e) =>
//                                 updateRow(row.course_subject_id, 'end_time', e.target.value)
//                               }
//                               onFocus={inputFocus}
//                               onBlur={inputBlur}
//                             />
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               </Card>

//               <div className="d-flex flex-wrap gap-2 justify-content-end pt-2">
//                 <Button
//                   variant="light"
//                   type="button"
//                   onClick={() => navigate('/ExamSheet')}
//                   className="border"
//                   style={{
//                     borderRadius: '8px',
//                     borderColor: theme.border,
//                     minWidth: '120px',
//                     fontWeight: 600,
//                     fontFamily: 'Inter, system-ui, sans-serif',
//                   }}
//                 >
//                   Cancel
//                 </Button>
//                 <Button
//                   type="submit"
//                   disabled={saving}
//                   style={{
//                     background: theme.ink,
//                     border: 'none',
//                     borderRadius: '8px',
//                     minWidth: '180px',
//                     fontWeight: 600,
//                     fontFamily: 'Inter, system-ui, sans-serif',
//                   }}
//                 >
//                   {saving ? (
//                     <>
//                       <Spinner animation="border" size="sm" className="me-2" />
//                       Saving…
//                     </>
//                   ) : (
//                     'Save all schedules'
//                   )}
//                 </Button>
//               </div>
//             </Form>
//           )}
//         </div>
//       </div>
//     </>
//   )
// }
